<script setup>

import { useRoute, useRouter } from "vue-router";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { useWebsiteStore } from "@/stores/website";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Spinner from "@/components/Spinner.vue";
import categoryMapping from "@/data/categoryMapping";
import { useUserStore } from "@/stores/user";
import categoryGrouping from "@/data/categoryGrouping";
import AiIssueDetailedFix from "@/views/preview/sections/AiIssueDetailedFix.vue";

const collapseRef = ref(null)
// Unique id per component instance to scope the inner issues accordion
const issuesAccordionId = `ai-issue-list-accordion-${Math.random().toString(36).slice(2)}`
const websiteStore = useWebsiteStore();
const {website, ignoredIssues} = storeToRefs(websiteStore);
const loaded = ref(false);

const activeDeviceRender = useActiveDeviceRenderStore();
const { proofreadIssues } = storeToRefs(activeDeviceRender);
const router = useRouter()
const route = useRoute()
const props = defineProps({
  devicePreview: Object,
  title: String,
  icon: {
    type: String,
    default: 'fa fa-magnifying-glass-chart'
  },
  categories: {
    type: Array,
    default: []
  },
  allowIgnore: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:processedData']);

// ----------
const annotations = ref([])
const categories = ref([])
const status = ref("new")
const processedData = ref([])
const issueFilter = ref('all')

const feedbackGenerated = ref(false);
const aiProgress = ref(0.0)

let allAnnotations = {}
let firstView = true;
let oldJsonData = ''

function setAnnotations(scroll = true) {
  setTimeout(() => {
    annotations.value = allAnnotations[activeDeviceRender.devicePreview.deviceName.toLowerCase()]
    showBoxes(scroll)
    highlightSelectedFeedback()
    let issue = getIssueFromBoxId(route.query.i)
    if (!issue) {  // can happen if there are no boxes, eg bot crawling issue
      issue = getIssueFromId(route.query.i);
    }
    openIssue(issue.i)
  }, 100)
}

watch(
    [proofreadIssues, () => activeDeviceRender.loaded],
    ([issues, activeDeviceRenderLoaded]) => {
      if (!activeDeviceRenderLoaded) {
        return
      }

      loaded.value = true;

      if (!issues || ['completed', 'error'].indexOf(issues.status) === -1) {
        status.value = "new"
        annotations.value = []
        categories.value = []
        return
      }
      feedbackGenerated.value = true

      if (['proofread'].includes(route.query.section)) {
        status.value = issues.status;
      } else if (['ai_readiness'].includes(route.query.section)) {
        status.value = issues.ai_readiness_status;
      }
      let jsonData = issues?.processed_data ? issues.processed_data : '';
      let jsonDataAiReadiness = issues?.processed_ai_readiness ? issues.processed_ai_readiness : '';
      let processedProofreadingItems = (JSON.parse(JSON.stringify(jsonData)) || {}).result || [];
      for (let entry of processedProofreadingItems) {
        entry.category = `proofread.${entry.category}`
      }
      let processedAiReadinessItems = (JSON.parse(JSON.stringify(jsonDataAiReadiness)) || {}).result || [];
      for (let entry of processedAiReadinessItems) {
        entry.category = `ai_readiness.${entry.category}`
      }
      processedData.value = processedProofreadingItems;
      processedData.value = processedData.value.concat(processedAiReadinessItems)

      processedData.value = processedData.value.filter((issue) => {
        return props.categories.includes(issue.category)
      })

      emit('update:processedData', processedData.value)

      allAnnotations = {
        mobile: [],
        tablet: [],
        desktop: [],
      }
      for (let issue of processedData.value) {
        if (issue.severity === 'high') {
          issue.level = 'error'
        } else if (issue.severity === 'medium') {
          issue.level = 'warning'
        } else {
          issue.level = 'suggestion'
        }

        issue._showDetailedFix = false;

        allAnnotations.mobile.push(issue.annotations.mobile)
        allAnnotations.tablet.push(issue.annotations.tablet)
        allAnnotations.desktop.push(issue.annotations.desktop)
      }

      // allAnnotations = data.ai_proofread[0].annotations || {}
      setAnnotations(jsonData !== oldJsonData)

      if (['completed', 'error'].indexOf(status.value) !== -1) {
        aiProgress.value = 100
      }

      if (jsonData !== oldJsonData) {
        oldJsonData = jsonData;

        setTimeout(() => {

          let q = route.query
          if (!q['i']) {
            let v = filteredIssues.value.filter((issue) => {
              let focusCategories = [route.query.focus];
              if (categoryGrouping[route.query.focus]) {
                focusCategories = categoryGrouping[route.query.focus].innerCategories;
              }
              if (focusCategories.includes(`${route.query.section}.${issue.category}`)) {
                if (route.query.focus_priority) {
                  return issue.severity === route.query.focus_priority;
                }
                return true;
              }
            });
            if (v.length > 0) {
              q['i'] = v[0].i;
            } else if (filteredIssues.value.length > 0) {
              q['i'] = filteredIssues.value[0].i;
            }
          }
          delete q['focus'];
          delete q['focus_priority'];
          router.replace({query: q, force: true});
          highlightSelectedFeedback(false)
        }, 0)
      }
      issueFilter.value = 'all'

    },
    {
      immediate: true,
    }
);

const filteredAnnotations = computed(() => {

  let indexes = filteredIssues.value.map((v) => v.i)
  return annotations.value.filter((v) => v && indexes.indexOf(v.i) !== -1)
})
let boxes = []

function showBoxes(scroll = true) {

  boxes = []
  let query = {}
  let count = 0;
  let index = 0;
  let ratio = props.devicePreview.ratio();
  let platform = activeDeviceRender.devicePreview.deviceName.toLowerCase();

  for (let annotation of filteredAnnotations.value) {
    count = annotation.i;
    index = annotation.i;
    let q = {}
    Object.assign(q, query);
    q['i'] = index;
    q['section'] = route.query.section;
    q['s'] = route.query.s;
    let id = `highlight-box-inactive-${count}`
    id = `highlight-box-ai-${platform}-${count}`

    let color = 'red'
    let sentiment = 'negative'
    let issue = processedData.value.find((issue) => issue.i === annotation.i)
    if (issue.level === 'warning') {
      color = 'orange'
      sentiment = 'warning'
    } else if (issue.level === 'suggestion') {
      color = 'gray'
      sentiment = 'suggestion'
    }

    let annotation_boxes = [annotation.box]
    let lineBox = {}
    if (annotation.box2) {
      if (JSON.stringify(annotation.box) !== JSON.stringify(annotation.box2)) {
        annotation_boxes.push(annotation.box2)
        if ((annotation.box.top + annotation.box.height) < annotation.box2.top) {
          lineBox = {
            'left': (annotation.box.left + annotation.box.width / 2) * ratio,
            'top': (annotation.box.top + annotation.box.height + 5) * ratio,
            'bottom': (annotation.box2.top - 5) * ratio,
            'right': (annotation.box2.left + annotation.box2.width / 2) * ratio,
          }
        } else {
          lineBox = {
            'left': (annotation.box2.left + annotation.box2.width / 2) * ratio,
            'top': (annotation.box2.top + annotation.box2.height + 5) * ratio,
            'bottom': (annotation.box.top - 5) * ratio,
            'right': (annotation.box.left + annotation.box.width / 2) * ratio,
          }
        }
      }
    }

    let boxIndex = 0;

    for (let annotation_box of annotation_boxes) {

      boxIndex += 1;
      q = Object.assign({}, q);
      q['b'] = boxIndex
      boxes.push({
        'id': id,
        'key': `${id}-${route.query.s}-${issueFilter.value}`,
        'i': `${annotation.i}`,
        'b': boxIndex,
        'issueId': annotation.i,
        'box': annotation_box,
        'context': 'dffd',
        'highlight': false,
        'level': '',
        'active': route.query.i === `${index}`,
        'visible': true,
        'query': q,
        'style': {
          'visibility': annotation.default ? 'hidden' : 'visible',
          'left': `${annotation_box.left * ratio}px`,
          'top': `${annotation_box.top * ratio}px`,
          'width': `${annotation_box.width * ratio}px`,
          'height': `${annotation_box.height * ratio}px`,
        },
        'line': {
          'show': boxIndex === 2,
          'box': lineBox,
        },
        'variant': {
          'type': 'rough',
          'style': annotation.default ? 'circle' : 'box',
          'sentiment': sentiment,
          'color': color,
        }
      })
    }
  }

  props.devicePreview.showBoxes(boxes)
  if (scroll) {
    if (!route.query.scroll) {
      let box = boxes.find((b) => b.issueId == route.query.i)
      if (route.query.b) {
        box = boxes.find((b) => b.issueId == route.query.i && b.b == route.query.b)
      }
      if (box) {
        props.devicePreview.scrollToBox(box.box)
      }
    }
  }
}

function highlightSelectedFeedback(scroll = true) {
  let feedbackElement = document.getElementById('feedback');
  if (feedbackElement) {
    let aItems = feedbackElement.querySelectorAll('li');

    // Loop through each element and remove the class
    aItems.forEach(function (li) {
      li.classList.remove('selected');
    });

    let activeElement = feedbackElement.querySelector(`a[href="?section=${route.query.section}&i=${route.query.i}"]`);
    if (activeElement) {
      let liElement = activeElement.parentElement;
      if (liElement.nodeName !== 'LI') {
        liElement = liElement.parentElement;
      }
      liElement.classList.add('selected')
      if (scroll) {
        setTimeout(() => {
          liElement.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
        }, 1);
      }
    } else {
      let activeElement = feedbackElement.querySelector(`li[data-id="${route.query.i}"]`);
      if (activeElement) {
        activeElement.classList.add('selected')
      }
    }
  }
}


watch(
    () => props.devicePreview.mouseOverIndex,
    (newVal, oldVal) => {


      for (let issue of processedData.value) {
        issue.hover = false;
      }

      let issue = (getIssueFromBoxId(props.devicePreview.mouseOverIndex))

      if (issue) {
        issue.hover = true;
      }
    },
    {
      immediate: true
    });

watch(() => route.query, (query) => {
  if (['proofread', 'ai_readiness'].includes(query.section)) {
    showBoxes()
    highlightSelectedFeedback()
    let issue = getIssueFromBoxId(query.i)
    openIssue(issue.i)
  }
})

watch(() => route.params, (newp, oldp) => {
  if (newp.deviceId !== oldp.deviceId) {
    annotations.value = []
    showBoxes()
    setAnnotations()
  }
})

// ----------
function getIssueFromId(issueId) {
  return processedData.value.find((issue) => issue.i === issueId) || false;
}

function getIssueFromBoxId(boxId) {
  let box = boxes.find((b) => b.i == boxId)
  if (!box) {
    return false;
  }
  return processedData.value.find((issue) => issue.i === box.issueId)
}

const screenshotReady = computed(() => !!activeDeviceRender.devicePreview.screenshotFull.url)

function filterIssuesBySeverity(severity) {
  let issues = processedData.value.filter((issue) => {
    if (ignoredIssues.value.proofread.includes(issue.issue_id)) {
      return false;
    }
    if (severity === 'all') {
      return true;
    }
    return issue.severity === severity
  });

  const platform = 'tablet';

  return issues.sort((a, b) => {
    const aBox = a.annotations[platform].box;
    const bBox = b.annotations[platform].box;

    if (!aBox || !bBox) {
      return 0;
    }
    return aBox.top - bBox.top;
  });
}

const filteredIssues = computed(() => {
  return filterIssuesBySeverity(issueFilter.value);
})

const isOpen = ref(true)


onMounted(() => {

  const el = collapseRef.value
  if (!el) return

  const onShown = (e) => {
    if (e.target !== el) return
    isOpen.value = true;
    showBoxes()
  }
  const onHidden = (e) => {
    if (e.target !== el) return
    isOpen.value = false;
  }

  el.addEventListener('shown.bs.collapse', onShown)
  el.addEventListener('hidden.bs.collapse', onHidden)

  onBeforeUnmount(() => {
    el.removeEventListener('shown.bs.collapse', onShown)
    el.removeEventListener('hidden.bs.collapse', onHidden)
  })
})

function toggleProofreadingAccordion(item) {
  const q = {...route.query}
  q['i'] = item.i;
  let box = boxes.find((a) => a.issueId === item.i)
  if (box) {
    q['i'] = box.i
    q['b'] = 1;

  }
  router.replace({query: q})
}

function openIssue(issueId) {
  // let issue = getIssueFromBoxId()
  const id = `details_copy_${issueId}`;
  const el = document.getElementById(id);
  if (!el) return;

  // Avoid auto-toggling on init; we’ll control it with show()
  const collapse = bootstrap.Collapse.getOrCreateInstance(el, {toggle: false});
  collapse.show();
}

async function ignoreIssue(issue) {
  const userStore = useUserStore();
  if (userStore.user.role === 'guest') {
    userStore.showNotification('login')
    return
  }


  let q = {};
  Object.assign(q, route.query);
  q['i'] = null
  await router.push({query: q})
  let normalizedIssue = Object.assign({}, issue);
  normalizedIssue['category'] = issue['category'];
  issueFilter.value = 'loading'
  showBoxes(false);
  await websiteStore.ignoreIssue(normalizedIssue, activeDeviceRender.page.externalId)
}

function issueLabel(issue) {
  if (issue.label) {
    return issue.label;
  }
  const [cat, detail] = issue.category.split(/\.(.+)/);
  return categoryMapping[cat][detail]?.label
}


function getCrawlabilityStatus() {
  let page = activeDeviceRender.page;
  if (!page.crawlRules) return []

  const botGroups = [
    {label: 'ChatGPT', icon: '/assets/images/openai-brands-solid-full.svg', bots: ['chatgpt-user', 'gptbot']},
    {label: 'Gemini', icon: '/assets/images/gemini-color.svg', bots: ['gemini-deep-research']},
    {label: 'Grok', icon: '/assets/images/grok.svg', bots: ['grokbot']},
    {label: 'Perplexity', icon: '/assets/images/perplexity.svg', bots: ['perplexity-user', 'perplexitybot']},
    {label: 'Claude', icon: '/assets/images/claude.svg', bots: ['claude-searchbot', 'claudebot', 'claude-user']},
    {label: 'Search Bots', icon: '/assets/images/google.svg', bots: ['googlebot', 'bingbot', 'ccbot']}
  ]

  return botGroups.map(group => {
    // If any bot in the group is blocked, consider the group blocked
    const blockingBots = group.bots
        .map(bot => {
          const rule = page.crawlRules[bot] || {}
          return {bot, rule: rule.crawl || {}}
        })
        .filter(item => item.rule.allowed === false)

    const isAllowed = blockingBots.length === 0

    let detailedStatus = `${group.label}: ${isAllowed ? 'Allowed' : 'Blocked'}`
    if (!isAllowed) {
      const blockDetails = blockingBots.map(item => {
        const details = []
        if (item.rule.source) details.push(`Source: ${item.rule.source}`)
        if (item.rule.directive) details.push(`Directive: ${item.rule.directive}`)
        return `${item.bot}${details.length > 0 ? ' (' + details.join(', ') + ')' : ''}`
      }).join(', ')
      detailedStatus += `\nBlocked by: ${blockDetails}`
    }

    return {
      name: group.label,
      label: group.label,
      icon: group.icon,
      allowed: isAllowed,
      tooltip: detailedStatus
    }
  })
}
</script>

<template>
  <div v-if="!loaded" class="ms-2 p-4 text-center">
    <p class="text-muted">Loading..</p>
    <spinner style="width:24px;height:24px"></spinner>
  </div>
  <div v-else>
    <template v-if="aiProgress === 100">

      <div v-if="filterIssuesBySeverity('all').length > 0">
        <div class="d-flex justify-content-between align-items-center mb-0 mt-2 p-2 border-bottom issues-found-heading">
          <div class="h6 mb-0"><b>{{ filterIssuesBySeverity('all').length }}</b>
            {{ filterIssuesBySeverity('all').length === 1 ? 'Issue' : 'Issues' }} Found
          </div>
          <div class="d-flex gap-2">
                <span v-if="filterIssuesBySeverity('high').length > 0"
                      class="badge badge-danger clickable"
                      :class="{'active-priority': issueFilter === 'high'}"
                      @click.prevent="issueFilter = issueFilter === 'high' ? 'all' : 'high'; showBoxes(false)">
                  {{ filterIssuesBySeverity('high').length }} High
                </span>
            <span v-if="filterIssuesBySeverity('medium').length > 0"
                  class="badge badge-warning clickable"
                  :class="{'active-priority': issueFilter === 'medium'}"
                  @click.prevent="issueFilter = issueFilter === 'medium' ? 'all' : 'medium'; showBoxes(false);">
                  {{ filterIssuesBySeverity('medium').length }} Med
                </span>
            <span v-if="filterIssuesBySeverity('low').length > 0"
                  class="badge badge-info clickable"
                  :class="{'active-priority': issueFilter === 'low'}"
                  @click.prevent="issueFilter = issueFilter === 'low' ? 'all' : 'low'; showBoxes(false)">
                  {{ filterIssuesBySeverity('low').length }} Low
                </span>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="p-4 m-2 bg-success-subtle border border-success border-opacity-10 rounded-3 mb-3 text-center">
          <div class="mb-2">
            <span class="fa fa-circle-check text-success fs-2"></span>
          </div>
          <h5 class="fw-bold text-success-emphasis mb-2">No Issues Detected</h5>
          <p class="text-muted small mb-0" v-if="route.query.section === 'ai_readiness'">
            Your page is accessible to AI crawlers and structured in a clear understandable way.
          </p>
          <p class="text-muted small mb-0" v-else-if="route.query.section === 'proofread'">
            All content on your page is consistent and without grammar or tone issues.
          </p>
        </div>
      </div>

      <ul class="list-group list-group-flush max-height-overflow" style="max-height: 550px;">
        <div class="pt-0 mt-0">
          <div class="text-center p-3" v-if="issueFilter === 'loading'">
            <spinner></spinner>
          </div>

          <div class="list-group" :id="issuesAccordionId">
            <div class="list-group-item" v-for="issue in filteredIssues">

              <div :class="{'issue-header-hover': issue.hover}">
                <div class="issue-header collapsed ps-2" data-bs-toggle="collapse"
                     :data-bs-target="`#details_copy_${issue.i}`"

                     aria-expanded="false" @click="toggleProofreadingAccordion(issue)">
                  <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                  <span class="fa fa-square me-1 issue-icon-fixed-width"
                        :class="{[`proofreading-text-${issue.level}`]: 1}"></span>
                    <strong class="issue-type-header">{{
                        issueLabel(issue)
                      }}:</strong>
                    <span class="text-truncate text-muted" v-if="issue.current_copy">"{{
                        issue.current_copy
                      }}"</span>
                  </div>
                  <i class="fa-solid fa-chevron-right chevron me-1"></i>
                </div>
              </div>
              <div :id="`details_copy_${issue.i}`" class="collapse" :data-bs-parent="`#${issuesAccordionId}`">
                <div class="details ps-4" :class="{[`proofreadindg-body-${issue.level}`]: 1}">
                  <p>
                    <div v-if="allowIgnore" class="dropdown dropdown-menu-sm-end"
                         style="float:right;top:-2px;margin-right: -6px;">
                      <a href="#" class="text-secondary dropdown-toggle" role="button" data-bs-toggle="dropdown"
                         aria-expanded="false" title="Options" aria-label="Options menu">
                        <span class="fa fa-gear text-muted"></span>
                      </a>
                      <ul class="dropdown-menu dropdown-menu-end dropdown-menu-md text-small">
                        <li>
                          <a class="dropdown-item" href="" @click.prevent="ignoreIssue(issue)">
                            <span class="fa fa-minus-circle me-2"></span> Ignore this Issue.</a>
                        </li>

                      </ul>
                    </div>
                    <template v-if="issue.category === 'text_quality'">
                      <strong>Current: </strong>
                      <span v-if="issue.current_copy && issue.current_copy.length > 60"
                            class="show-more text-muted">
                                              <span v-if="!issue._showMore">"{{
                                                  issue.current_copy.slice(0, 60)
                                                }}… </span>
                                              <span v-else>"{{ issue.current_copy }}"</span>
                                              <a class="toggle-btn ms-1"
                                                 @click.stop.prevent="issue._showMore = !issue._showMore">{{
                                                  issue._showMore ? 'Show less' : 'Show more'
                                                }}</a>
                                            </span>
                      <span class="text-muted" v-else>"{{ issue.current_copy }}"</span>
                    </template>
                  </p>

                    <p class="d-flex gap-0" v-if="issue.category === 'ai_readiness.crawlability'">
                      <strong class="me-1">Bot Status: </strong>
                      <template v-for="bot in getCrawlabilityStatus()" :key="bot.name">
                        <div class="position-relative d-inline-flex align-items-center justify-content-center"
                             v-tooltip="bot.tooltip" style="width: 20px; height: 20px;">
                          <img :src="bot.icon"
                               :alt="bot.label"
                               :style="{
                           width: '20px',
                           height: '20px',
                           padding: '1px',
                           opacity:  bot.allowed ? '0.72': '0.5',
                           objectFit: 'contain',
                           filter: bot.allowed ? 'brightness(0)' : 'invert(12%) sepia(94%) saturate(7449%) hue-rotate(0deg) brightness(91%) contrast(115%)',
                         }">
                        </div>
                      </template>
                    </p>

                  <p>

                    <strong>Explanation:</strong> <span>{{ issue.explanation }}</span>
                  </p>

                  <div v-if="issue.llm_issue_details" class="mb-3">
                    <strong>LLM Issue:</strong> <span style="white-space: pre-wrap;">{{
                      issue.llm_issue_details
                    }}</span>
                  </div>


                  <div class="mb-3">
                    <p class="mb-2">
                      <template v-if="issue.category.includes('text_quality.')">
                        <strong>Suggested Fix:</strong> {{ issue.suggested_copy }}
                      </template>
                      <template v-else>
                        <strong>Suggestion:</strong> {{ issue.suggestion }}
                      </template>
                    </p>

                    <ai-issue-detailed-fix :issue="issue"/>
                  </div>

                </div>

              </div>
            </div>


          </div>

        </div>
      </ul>
    </template>
    <div v-else style="font-size: 0.875em">
      <div v-if="activeDeviceRender.page.httpStatusOkOrPending">
        <div class="p-3 text-center">
          <div class="mb-0 mt-2">
            <div class="text-muted" v-if="route.query.section === 'ai_readiness'"><spinner></spinner> Evaluating AI Readiness of your page.</div>
            <div class="text-muted" v-else>
             <spinner></spinner> Performing a comprehensive audit of your content and
              structure.</div>
          </div>

          <div class="row g-2 text-start mt-0">
            <!-- AI Readiness specific loading -->
            <template v-if="route.query.section === 'ai_readiness'">
              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0"
                       :class="categoryGrouping.ai_readiness.color">
                    <i class="fa fa-robot "></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Crawlability</h6>
                    <p class="mb-0 text-muted small">Verifying if major LLM bots like GPTBot can index your site.</p>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0"
                       :class="categoryGrouping.ai_readiness.color"
                       style="--processing-color: #0dcaf0">
                    <i class="fa fa-code "></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Semantic Hierarchy</h6>
                    <p class="mb-0 text-muted small">Checking if your content structure provide clear
                      context for AI agents.</p>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 py-2 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0"
                       :class="categoryGrouping.ai_readiness.color"
                       style="--processing-color: #198754">
                    <i class="fa fa-eye "></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Visual Context</h6>
                    <p class="mb-0 text-muted small">Ensuring important information is not hidden
                      from ai crawlers.</p>
                  </div>
                </div>
              </div>
            </template>

            <!-- Proofreading specific loading -->
            <template v-else>
              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0" :class="categoryGrouping.proofread.color"
                       style="--processing-color: #198754">
                    <i class="fa fa-spell-check"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Spelling & Grammar</h6>
                    <p class="mb-0 text-muted small">Scanning for typos, readability issues and grammatical errors that
                      might impact your page.</p>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div class="d-flex align-items-start gap-3 p-3 border rounded bg-light-subtle">
                  <div class="icon-shape rounded is-processing flex-shrink-0" :class="categoryGrouping.integrity.color"
                       style="--processing-color: #0dcaf0">
                    <i class="fa fa fa-pen-fancy"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 fw-bold">Content Integrity</h6>
                    <p class="mb-0 text-muted small">Scanning for conflicting facts, missing elements, mislabeled images
                      and visible errors.</p>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="mt-4 text-muted small italic">
            <i class="fa fa-circle-notch fa-spin"></i> This usually takes less than a minute.
          </div>
        </div>
      </div>
      <div v-else>
        <div class="alert alert-danger" role="alert">
          <p class="mb-0"><b>Proofreading not available.</b><br> We could not load this page, the server responded
            with a status
            code of {{ activeDeviceRender.page.httpStatusCode }}. </p>
        </div>
      </div>
    </div>
  </div>

</template>

<style scoped>
.issue-icon-fixed-width {
  width: 20px;
  display: inline-block;
  text-align: center;
}



.bg-soft-primary {
  background-color: rgba(13, 110, 253, 0.1);
}

.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-soft-info {
  background-color: rgba(13, 202, 240, 0.1);
}

.transition-all {
  transition: all 0.5s ease;
}

.pulse-active {
  animation: pulse-border 2s infinite;
}

@keyframes pulse-border {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

@property --angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@keyframes spin-border {
  from {
    --angle: 0deg;
  }
  to {
    --angle: 360deg;
  }
}
</style>

<style>
/* Smaller list items when inside MCP layout - unscoped to reach parent */
.mcp-layout-parent .list-group-item {
  padding: 0.2rem 0;
  font-size: 0.875rem;
}
.mcp-layout-parent .issues-found-heading{
  font-size: 90%;
  .h6{
    font-size: 14px;
    margin-left: 12px;
  }
}

.mcp-layout-parent .issue-header {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

.mcp-layout-parent .issue-text {
  font-size: 0.85rem;
}

.mcp-layout-parent .details {
  font-size: 0.8125rem;
  padding: 0.5rem 0.75rem;
}

.mcp-layout-parent .details p {
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
}

.mcp-layout-parent .issue-type-header {
  font-size: 0.85rem;
}
</style>